"use client";

import type { ThemeProviderProps } from "next-themes";

import * as React from "react";
import { HeroUIProvider } from "@heroui/system";
import { ToastProvider } from "@heroui/toast";
import { useRouter } from "next/navigation";
import { ClerkProvider } from "@clerk/nextjs";
import { dark } from "@clerk/themes";
import { ThemeProvider } from "../components/theme-provider";
import { useTheme } from "next-themes";
import ServerActionErrorHandler from "../components/ServerActionErrorHandler";
import DeploymentDetector from "../components/DeploymentDetector";
import SessionWatcher from "../components/SessionWatcher";
import SupportSessionBadge from "../components/SupportSessionBadge";

export interface ProvidersProps {
  children: React.ReactNode;
  themeProps?: ThemeProviderProps;
}

declare module "@react-types/shared" {
  interface RouterConfig {
    routerOptions: NonNullable<
      Parameters<ReturnType<typeof useRouter>["push"]>[1]
    >;
  }
}

function ClerkThemeWrapper({ children }: { children: React.ReactNode }) {
  const { resolvedTheme } = useTheme();

  return (
    <ClerkProvider
      appearance={{
        baseTheme: resolvedTheme === "dark" ? dark : undefined,
        elements: { impersonationFab: { display: "none" } },
      }}
    >
      {children}
      <SupportSessionBadge />
    </ClerkProvider>
  );
}

export function Providers({ children, themeProps }: ProvidersProps) {
  const router = useRouter();
  const showDeploymentDetector = process.env.NODE_ENV === "production";

  return (
    <ThemeProvider {...themeProps}>
      <ServerActionErrorHandler />
      {showDeploymentDetector ? <DeploymentDetector /> : null}
      <ClerkThemeWrapper>
        <SessionWatcher />
        <HeroUIProvider navigate={router.push}>
          <ToastProvider />
          {children}
        </HeroUIProvider>
      </ClerkThemeWrapper>
    </ThemeProvider>
  );
}
